import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, ArrowLeft } from "lucide-react";
import { Loader2 } from "./ui/loader2";

const API = process.env.REACT_APP_BACKEND_URL;

export const SupportChatWidget = ({ token: tokenProp = null }) => {
  // Logged-in customer admins (also when browsing public pages) get the live customer chat automatically.
  const token = tokenProp || (() => { try { return localStorage.getItem("adminToken") && localStorage.getItem("adminUser") ? localStorage.getItem("adminToken") : null; } catch { return null; } })();
  const [open, setOpen] = useState(() => { try { return new URLSearchParams(window.location.search).get("support") === "1"; } catch { return false; } });
  useEffect(() => {
    const h = (e) => { if (new URLSearchParams(e.detail?.search || "").get("support") === "1") setOpen(true); };
    window.addEventListener("servora:push-navigate", h);
    return () => window.removeEventListener("servora:push-navigate", h);
  }, []);
  const [mode, setMode] = useState(null); // null = choose, "visitor", "tenant"
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const [ws, setWs] = useState(null);
  const [online, setOnline] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [formSent, setFormSent] = useState(false);
  const [formError, setFormError] = useState("");
  const messagesEnd = useRef(null);

  const scrollToBottom = () => messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(scrollToBottom, [messages]);

  // Connect WebSocket for tenant mode
  const connectWs = useCallback((id) => {
    const wsUrl = `${API?.replace("https://", "wss://").replace("http://", "ws://")}/api/support-chat/ws/${id}`;
    const socket = new WebSocket(wsUrl);
    socket.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "message") {
          setMessages(prev => [...prev, data.message]);
        } else if (data.type === "presence") {
          setOnline(!!data.online);       // support goes online/offline while the chat is open
        }
      } catch {}
    };
    socket.onclose = () => setTimeout(() => connectWs(id), 3000);
    setWs(socket);
    return socket;
  }, []);

  // Tell the server whether the chat is really on screen, so a reply becomes a push when the window is closed or the app is in the background
  useEffect(() => {
    if (!ws) return undefined;
    const report = () => {
      try { if (ws.readyState === 1) ws.send(JSON.stringify({ type: "visibility", visible: !!open && document.visibilityState === "visible" })); } catch { /* ignore */ }
    };
    report();
    ws.addEventListener("open", report);
    document.addEventListener("visibilitychange", report);
    return () => { ws.removeEventListener("open", report); document.removeEventListener("visibilitychange", report); };
  }, [ws, open]);

  // Support availability + (for logged-in admins) automatic customer chat
  const startAuthenticated = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/support-chat/start-authenticated`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!data.success) return false;
      setChatId(data.chat_id); setTenantName(data.tenant_name); setMode("tenant");
      setMessages(data.messages?.length ? data.messages : [{
        id: "welcome", sender: "system", text: `Hej! Skriv ditt ärende så svarar vi så snart vi kan.`, timestamp: new Date().toISOString(),
      }]);
      connectWs(data.chat_id);
      return true;
    } catch { return false; }
  }, [token, connectWs]);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/support-chat/status`).then(r => r.json()).then(d => setOnline(!!d.online)).catch(() => setOnline(false));
    if (!mode && !chatId) {
      if (token) startAuthenticated().then(ok => { if (!ok) startChat("visitor"); });
      else startChat("visitor");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitContact = async () => {
    setFormError("");
    if (!form.name.trim() || !form.email.includes("@") || form.message.trim().length < 5) { setFormError("Fyll i namn, e-post och ett meddelande."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/support-chat/contact`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (res.ok) setFormSent(true); else setFormError((await res.json().catch(() => ({}))).detail || "Något gick fel. Försök igen.");
    } catch { setFormError("Kunde inte skicka. Försök igen."); }
    setLoading(false);
  };

  // Start chat session
  const startChat = async (selectedMode) => {
    if (selectedMode === "visitor") {
      setMode("visitor");
      setChatId(null);
      setMessages([{
        id: "welcome", sender: "support", text: "Hej! Välkommen till Servora Platform. Hur kan vi hjälpa dig?",
        timestamp: new Date().toISOString()
      }]);
      // Create visitor chat on first message
    } else if (selectedMode === "contact") {
      setMode("contact");
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput("");
    setLoading(true);

    const userMsg = { id: Date.now().toString(), sender: "client", text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);

    try {
      if (mode === "visitor") {
        // Create chat on first message if not exists
        let currentChatId = chatId;
        if (!currentChatId) {
          const startRes = await fetch(`${API}/api/support-chat/start`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ mode: "visitor", name: "Besökare" })
          });
          const startData = await startRes.json();
          currentChatId = startData.chat_id;
          setChatId(currentChatId);
        }
        const res = await fetch(`${API}/api/support-chat/${currentChatId}/message`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text, sender: "client" })
        });
        const data = await res.json();
        if (data.ai_reply) {
          setMessages(prev => [...prev, data.ai_reply]);
        }
      } else if (mode === "tenant" && chatId) {
        await fetch(`${API}/api/support-chat/${chatId}/message`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text, sender: "client" })
        });
      }
    } catch {}
    setLoading(false);
  };

  const reset = () => {
    setMode(null);
    setChatId(null);
    setMessages([]);
    setInput("");
    setTenantName("");
    if (ws) { try { ws.close(); } catch {} }
  };

  return (
    <>
      {/* Float button */}
      {!open && (
        <button onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 w-14 h-14 bg-[#074E3B] hover:bg-[#053c2d] text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105"
          data-testid="support-chat-btn">
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[340px] sm:w-[380px] h-[500px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          data-testid="support-chat-window">
          {/* Header */}
          <div className="bg-[#074E3B] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              {mode === "contact" && (
                <button onClick={() => { setMode("visitor"); setFormSent(false); }} className="hover:bg-white/20 rounded p-0.5">
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              <div>
                <p className="text-sm font-semibold">Servora Platform Support</p>
                <p className="text-[10px] text-blue-200">
                  {mode === "tenant" ? (online ? "Live chatt – support är online" : "Support är offline just nu") : mode === "contact" ? "Skicka ett meddelande" : "AI-assistent · svarar direkt"}
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="hover:bg-white/20 rounded p-1">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3">
            {/* Contact form */}
            {mode === "contact" && (
              <div className="pt-3 space-y-3" data-testid="chat-contact-form">
                {formSent ? (
                  <div className="text-center py-8">
                    <p className="text-sm font-semibold text-slate-800">Tack – vi har fått ditt meddelande.</p>
                    <p className="text-xs text-slate-500 mt-1">Vi svarar på {form.email} så snart vi kan.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-slate-600">Beskriv ditt ärende så svarar vi via e-post.</p>
                    {[["name", "Namn", "text"], ["email", "E-post", "email"]].map(([k, ph, type]) => (
                      <input key={k} type={type} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
                        className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#074E3B]" data-testid={`chat-contact-${k}`} />
                    ))}
                    <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Ditt meddelande" rows={4}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#074E3B] resize-none" data-testid="chat-contact-message" />
                    {formError && <p className="text-xs text-red-500">{formError}</p>}
                    <button onClick={submitContact} disabled={loading}
                      className="w-full bg-[#074E3B] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#053c2d] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="chat-contact-send">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Skicka
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Messages */}
            {(mode === "visitor" || mode === "tenant") && (
              <div className="space-y-2">
                {messages.map(msg => (
                  <div key={msg.id} className={`flex ${msg.sender === "client" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                      msg.sender === "client" ? "bg-[#074E3B] text-white" :
                      msg.sender === "admin" ? "bg-emerald-100 text-emerald-900" :
                      msg.sender === "system" ? "bg-amber-50 text-amber-800 border border-amber-200" :
                      "bg-slate-100 text-slate-800"
                    }`}>
                      <p className="whitespace-pre-wrap">{msg.text}</p>
                      <p className={`text-[10px] mt-1 ${msg.sender === "client" ? "text-blue-200" : "text-slate-400"}`}>
                        {new Date(msg.timestamp).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 rounded-xl px-3 py-2">
                      <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                    </div>
                  </div>
                )}
                {mode === "tenant" && online === false && (
                  <p className="text-[11px] text-slate-500 text-center pt-1">Support är offline just nu – vi ser ditt meddelande och svarar så snart vi kan.</p>
                )}
                <div ref={messagesEnd} />
              </div>
            )}
          </div>

          {/* Visitor paths: human via form, customers via login */}
          {mode === "visitor" && (
            <div className="px-3 pt-2 flex flex-col items-center gap-0.5 text-[11.5px] text-slate-500 flex-shrink-0">
              <button onClick={() => setMode("contact")} className="text-[#074E3B] font-semibold hover:underline" data-testid="chat-to-contact">Prata med en människa</button>
              <a href="/login" className="text-[#074E3B] font-semibold hover:underline" data-testid="chat-to-login">Kund? Logga in för live-support</a>
            </div>
          )}

          {/* Input */}
          {(mode === "visitor" || mode === "tenant") && (
            <div className="p-3 border-t border-slate-100 flex-shrink-0">
              <div className="flex gap-2">
                <input value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
                  placeholder="Skriv ditt meddelande..."
                  className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#074E3B]"
                  data-testid="chat-message-input" />
                <button onClick={sendMessage} disabled={loading || !input.trim()}
                  className="bg-[#074E3B] text-white rounded-lg px-3 hover:bg-[#053c2d] disabled:opacity-50"
                  data-testid="chat-send-btn">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};
